import {
  MAPPABLE_FIELD_LABELS,
  MAPPABLE_FIELDS,
  type InboundMappingDto,
  type MappingPreview,
} from '@performance-crm/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { CopyButton } from './IngestPage';
import {
  useCatalogs,
  useDeleteMapping,
  useInboundMappings,
  usePreviewMapping,
  useProjects,
  useUpsertMapping,
} from '@/lib/queries';

const SAMPLE =
  '{\n  "name": "Ana Torres",\n  "phone": "+51 987 654 321",\n  "email": "ana@example.com",\n  "project": "Castilla Park",\n  "utm_campaign": "SEP_LANDING",\n  "message": "Quiero información"\n}';

export function MappingsTab({ companyId }: { companyId: string }) {
  const apiBase = window.location.origin.replace(':5180', ':3610');
  const { data: mappings } = useInboundMappings(companyId);
  const [editing, setEditing] = useState<InboundMappingDto | 'new' | null>(null);
  const remove = useDeleteMapping();
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Nueva fuente
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {mappings?.map((m) => (
          <Card key={m.id} data-testid="mapping-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>
                  {m.name}{' '}
                  <code className="bg-muted ml-1 rounded px-1 text-xs">/inbound/{m.source}</code>
                </span>
                {!m.active && <Badge tone="muted">inactiva</Badge>}
              </CardTitle>
              <CardDescription>
                {Object.keys(m.fieldMap).length} campos mapeados · proyecto por defecto:{' '}
                {m.projectName ?? '—'}
                <span className="mt-1 block">
                  URL para el formulario:{' '}
                  <code className="text-xs">
                    {apiBase}/api/v1/inbound/{m.source}
                  </code>{' '}
                  <CopyButton value={`${apiBase}/api/v1/inbound/${m.source}`} /> · la clave va en la
                  cabecera <code className="text-xs">X-API-Key</code> o, si la herramienta solo
                  admite una URL, como <code className="text-xs">?key=…</code> (solo claves de
                  ingesta).
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(m)}>
                Editar y probar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => confirm('¿Borrar esta fuente?') && remove.mutate(m.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
        {mappings?.length === 0 && (
          <p className="text-muted-foreground text-sm md:col-span-2">
            Sin fuentes configuradas. Cada fuente define cómo se convierte su payload en un lead.
          </p>
        )}
      </div>
      {editing && (
        <MappingEditor
          companyId={companyId}
          mapping={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function MappingEditor({
  companyId,
  mapping,
  onClose,
}: {
  companyId: string;
  mapping: InboundMappingDto | null;
  onClose: () => void;
}) {
  const { data: projects } = useProjects();
  const { data: catalogs } = useCatalogs(companyId);
  const upsert = useUpsertMapping();
  const preview = usePreviewMapping();
  const toast = useToast();
  const [source, setSource] = useState(mapping?.source ?? '');
  const [name, setName] = useState(mapping?.name ?? '');
  const [projectId, setProjectId] = useState(mapping?.projectId ?? '');
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({
    ...(mapping?.fieldMap ?? {}),
  });
  const [defaults, setDefaults] = useState<Record<string, string>>({
    ...(mapping?.defaults ?? {}),
  });
  const [sample, setSample] = useState(SAMPLE);
  const [result, setResult] = useState<MappingPreview | null>(null);
  const [active, setActive] = useState(mapping?.active ?? true);

  const test = async () => {
    try {
      const payload = JSON.parse(sample);
      setResult(
        await preview.mutateAsync({
          companyId,
          fieldMap,
          defaults,
          projectId: projectId || null,
          payload,
        }),
      );
    } catch (err) {
      toast.error('No se pudo probar', (err as Error).message);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await upsert.mutateAsync({
        companyId,
        source,
        name,
        fieldMap,
        defaults,
        projectId: projectId || null,
        active,
      });
      toast.success('Fuente guardada');
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const clean = (r: Record<string, string>) =>
    Object.fromEntries(Object.entries(r).filter(([, v]) => v));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{mapping ? `Editar ${mapping.name}` : 'Nueva fuente'}</CardTitle>
        <CardDescription>
          Ruta = dónde está el dato en el JSON (puntos y corchetes: data.fields[0].value). Valor por
          defecto = si el payload no lo trae.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="mp-source">Fuente (URL)</Label>
                <Input
                  id="mp-source"
                  value={source}
                  onChange={(e) => setSource(e.target.value.toLowerCase())}
                  placeholder="landing, n8n, urbania…"
                  required
                  disabled={!!mapping}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mp-name">Nombre</Label>
                <Input
                  id="mp-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="mp-project">Proyecto por defecto</Label>
                <Select
                  id="mp-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">— (debe venir en el payload)</option>
                  {projects
                    ?.filter((p) => p.companyId === companyId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </Select>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
                  <tr>
                    <th className="px-2 py-1">Campo del lead</th>
                    <th className="px-2 py-1">Ruta en el payload</th>
                    <th className="px-2 py-1">Valor por defecto</th>
                  </tr>
                </thead>
                <tbody>
                  {MAPPABLE_FIELDS.map((f) => (
                    <tr key={f} className="border-t">
                      <td className="px-2 py-1 text-xs">{MAPPABLE_FIELD_LABELS[f]}</td>
                      <td className="px-2 py-1">
                        <Input
                          className="h-8"
                          value={fieldMap[f] ?? ''}
                          onChange={(e) => setFieldMap((m) => clean({ ...m, [f]: e.target.value }))}
                          aria-label={`ruta ${f}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <Input
                          className="h-8"
                          value={defaults[f] ?? ''}
                          onChange={(e) => setDefaults((m) => clean({ ...m, [f]: e.target.value }))}
                          aria-label={`default ${f}`}
                        />
                      </td>
                    </tr>
                  ))}
                  {(catalogs?.customFields ?? []).map((f) => {
                    const k = `custom.${f.key}`;
                    return (
                      <tr key={k} className="border-t">
                        <td className="px-2 py-1 text-xs">Campo: {f.label}</td>
                        <td className="px-2 py-1">
                          <Input
                            className="h-8"
                            value={fieldMap[k] ?? ''}
                            onChange={(e) =>
                              setFieldMap((m) => clean({ ...m, [k]: e.target.value }))
                            }
                            aria-label={`ruta ${k}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            className="h-8"
                            value={defaults[k] ?? ''}
                            onChange={(e) =>
                              setDefaults((m) => clean({ ...m, [k]: e.target.value }))
                            }
                            aria-label={`default ${k}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4"
              />{' '}
              Fuente activa
            </label>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="mp-sample">Payload de ejemplo</Label>
              <Textarea
                id="mp-sample"
                value={sample}
                onChange={(e) => setSample(e.target.value)}
                rows={10}
                className="font-mono text-xs"
              />
            </div>
            <Button type="button" variant="outline" onClick={test} disabled={preview.isPending}>
              Probar mapeo
            </Button>
            {result && (
              <div className="rounded-md border p-3 text-sm" data-testid="mapping-result">
                <p className="font-medium">Resultado</p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  <li>
                    Nombre:{' '}
                    {[
                      result.mapped.firstName,
                      result.mapped.lastNamePaternal,
                      result.mapped.lastNameMaternal,
                    ]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </li>
                  <li>
                    Celular: {result.mapped.phone ?? '—'} · Correo: {result.mapped.email ?? '—'}
                  </li>
                  <li>
                    Proyecto: {result.projectName ?? '—'} · Medio: {result.mapped.medium ?? '—'} ·
                    Canal: {result.mapped.entryChannel ?? '—'}
                  </li>
                  <li>
                    UTM:{' '}
                    {[result.mapped.utmSource, result.mapped.utmMedium, result.mapped.utmCampaign]
                      .filter(Boolean)
                      .join(' / ') || '—'}
                  </li>
                  <li>
                    Identidad:{' '}
                    {result.identity.contactId
                      ? `ya existe (${result.identity.contactName}, por ${result.identity.matchType?.toLowerCase()})${result.identity.activeLeadInProject ? ' · con lead activo en el proyecto → recontacto sobre el mismo lead' : ' → recontacto'}${result.identity.doNotContact ? ' · pidió que no le escribamos (sin WhatsApp ni bot)' : ''}`
                      : 'persona nueva'}
                  </li>
                </ul>
                {result.issues.length > 0 && (
                  <ul className="text-danger mt-2 space-y-0.5 text-xs">
                    {result.issues.map((i) => (
                      <li key={i}>⚠ {i}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                Guardar fuente
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
